import React, { useEffect, useState, useContext, useMemo } from "react"
import { Modal } from "react-bootstrap"
import ClassNames from "classnames"
import {
  includes,
  get,
  map,
  isEmpty,
  filter,
  chunk,
  reverse,
  find,
  isArray,
} from "lodash"

import { SearchToggle } from "components/molecules/filter/toggleFilter"
import { searchStateType } from "components/actions/search"
import {
  BaseTable,
  SeparateTable,
  OptionTable,
} from "components/molecules/table"
import { tableColumns } from "config/table_columns"
import { CountryListType } from "components/utils/search"
import { SearchInput } from "components/atoms/input/input"
import { Btn } from "components/atoms/btn"
import { Pagination } from "components/molecules/pagination"
import { APIResponse } from "redux/apis/types"
import { PAGE_NUM, KEY_CODE, PUT_OR_CALL } from "config/const"
import APIs from "redux/apis"
import * as Formatter from "utils/Formatter"
import { createColumns } from "config/table_columns"
import { getCountryIcon } from "utils/util"
import { BaseSelect } from "components/atoms/input/select"
import { SearchCountryTypeContext } from "components/atoms/chart/parts/RelativeSymbolSelector"
import { checkIsSp } from "utils/util"
import uuid from "uuid"
import store from "redux/store"
import { pageLoadRegist } from "redux/ducks/PageLoad"

type Props = {
  show: boolean
  onHide: () => void
  result: APIResponse.Res
  selectCountry?: (id: number) => void
  state: searchStateType
  openSelectCountry?: boolean
  countryList: Array<CountryListType>
  selectAllCountry?: Function
  changeSearchValue: (str: string) => void
  load: Function
  currentPage: number
  changePage: (page: number) => void
  execute: (data: APIResponse.Res) => void
  chartType?: boolean
}

export const SearchResultModal: React.FC<Props> = props => {
  const [displaySelectCountry, changeSelectCountry] = useState<boolean>(
    !!props.openSelectCountry
  )
  const isSp = useMemo(() => {
    return checkIsSp()
  }, [])

  useEffect(() => {
    changeSelectCountry(props.state.isOpenSelectCountry)
  }, [props.state.isOpenSelectCountry])

  const resultList = get(props.result, "ResultList", [])

  return (
    <Modal {...props} size="lg">
      <Modal.Header>
        <div className="modal-header">
          <div className="header-title">検索</div>
          <div className="header-nav-icon-col">
            <div
              className="header-icon ripple"
              data-izimodal-close
              onClick={props.onHide}
            >
              <i className="material-icons">close</i>
            </div>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-body-content modal-search-result">
          <div className="control-row filter-control-row">
            <SearchInput
              className="filter-control-item floating-input-field"
              value={props.state.searchValue}
              onChange={(str: string) => props.changeSearchValue(str)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.keyCode === KEY_CODE.ENTER) {
                  props.changePage(0)
                  props.load()
                }
              }}
            />
            <Btn
              className="btn-primary btn-shaped"
              name="検索"
              onClick={() => {
                props.changePage(0)
                props.load()
              }}
            />
          </div>
          <div className="row-filter">
            <SearchToggle
              title="国選択"
              active={displaySelectCountry}
              onToggle={() => changeSelectCountry(!displaySelectCountry)}
            >
              <div
                className={ClassNames(
                  "panel-filter-list-col search-toggle-search-result",
                  {
                    "is-open": displaySelectCountry,
                  }
                )}
              >
                <div className="btn-multi-control">
                  <label
                    className="btn btn-outline btn-shaped"
                    onChange={() => props.selectAllCountry()}
                  >
                    <input
                      type="checkbox"
                      name="range"
                      className="input-multi-control"
                      checked={
                        props.state.selectCountries.length ===
                        props.countryList.length
                      }
                    />
                    <span className="ripple">すべて</span>
                  </label>
                  {props.countryList.map((li, i: number) => {
                    const isCheck = includes(props.state.selectCountries, li.id)
                    return (
                      <label
                        key={i}
                        className={ClassNames("btn btn-outline btn-shaped", {
                          disabled:
                            isCheck && props.state.selectCountries.length === 1,
                        })}
                        onChange={() => props.selectCountry(li.id)}
                      >
                        <input
                          type="checkbox"
                          name="range"
                          className="input-multi-control"
                          checked={isCheck}
                        />
                        <span className="ripple">{li.country}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            </SearchToggle>
          </div>
          <BaseTable
            className="table-striped table-search-result"
            list={
              isSp ? tableColumns.utils.searchSp : tableColumns.utils.search
            }
            data={resultList}
            onClick={(data: APIResponse.Res) => props.execute(data)}
          />
          <Pagination
            list={resultList}
            total={get(props.result, "TotalCount", null)}
            current={props.currentPage}
            page={!props.chartType ? PAGE_NUM.PAGE_20 : PAGE_NUM.PAGE_10}
            changePage={(page: number) => {
              props.changePage(page)
              props.load()
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="modal-bottom-btn-row">
          <div
            className="btn btn-outline ripple"
            data-izimodal-close
            onClick={props.onHide}
          >
            閉じる
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

const HEADER_LABEL = [
  { id: 0, name: "国内株式" },
  { id: 1, name: "外国株式" },
  { id: 2, name: "主要指数" },
  { id: 3, name: "国内指数" },
  { id: 4, name: "外国指数" },
  { id: 5, name: "先物" },
  { id: 6, name: "オプション" },
  { id: 7, name: "為替" },
  { id: 8, name: "商品" },
  { id: 9, name: "国債" },
]

const FOREIN_INDEX_MENU = [
  { id: 0, name: "米国", icon: "icon-us" },
  { id: 1, name: "香港", icon: "icon-hong-kong" },
  { id: 2, name: "中国", icon: "icon-china" },
  { id: 3, name: "ベトナム", icon: "icon-vietnam" },
  { id: 4, name: "韓国", icon: "icon-korea" },
  { id: 5, name: "台湾", icon: "icon-taiwan" },
  { id: 6, name: "シンガポール", icon: "icon-singapore" },
  { id: 7, name: "タイ", icon: "icon-thailand" },
  { id: 8, name: "マレーシア", icon: "icon-malaysia" },
  { id: 9, name: "インドネシア", icon: "icon-indonesia" },
  { id: 10, name: "フィリピン", icon: "icon-philippines" },
  { id: 11, name: "イスラエル", icon: "icon-israel" },
]

const BOND_MENU = [
  { id: 0, name: "タイ", icon: "icon-thailand" },
  { id: 1, name: "中国", icon: "icon-china" },
  { id: 2, name: "日本", icon: "icon-jp" },
  { id: 3, name: "香港", icon: "icon-hong-kong" },
  { id: 4, name: "米国", icon: "icon-us" },
  { id: 5, name: "南アフリカ", icon: "icon-south-africa" },
  { id: 6, name: "韓国", icon: "icon-korea" },
  { id: 7, name: "フィリピン", icon: "icon-philippines" },
  { id: 8, name: "インドネシア", icon: "icon-indonesia" },
  { id: 9, name: "台湾", icon: "icon-taiwan" },
  { id: 10, name: "英国", icon: "icon-uk" },
  { id: 11, name: "マレーシア", icon: "icon-malaysia" },
  { id: 12, name: "ドイツ", icon: "icon-germany" },
  { id: 13, name: "フランス", icon: "icon-france" },
  { id: 14, name: "シンガポール", icon: "icon-singapore" },
  { id: 15, name: "ニュージーランド", icon: "icon-new-zealand" },
  { id: 16, name: "オーストラリア", icon: "icon-au" },
  { id: 17, name: "ブラジル", icon: "icon-brazil" },
]

const FUTURE_MENU = [
  { id: 0, name: "日経225先物" },
  { id: 1, name: "日経225ミニ先物" },
  { id: 2, name: "JPX日経400先物" },
  { id: 3, name: "TOPIX先物" },
  { id: 4, name: "TOPIXミニ先物" },
  { id: 5, name: "CORE30先物" },
  { id: 6, name: "REIT先物" },
]

const FOREIN_INDEX_PARAM = [
  "FC.US.Index",
  "FC.HK.Index",
  "FC.CN.Index",
  "FC.VN.Index",
  "FC.KR.Index",
  "FC.TW.Index",
  "FC.SG.Index",
  "FC.TH.Index",
  "FC.MY.Index",
  "FC.ID.Index",
  "FC.PH.Index",
  "FC.IL.Index",
]

const BOND_PARAM = [
  "Top.GB.TH",
  "Top.GB.CN",
  "Top.GB.JP",
  "Top.GB.HK",
  "Top.GB.US",
  "Top.GB.ZA",
  "Top.GB.KR",
  "Top.GB.PH",
  "Top.GB.ID",
  "Top.GB.TW",
  "Top.GB.GB",
  "Top.GB.MY",
  "Top.GB.DE",
  "Top.GB.FR",
  "Top.GB.SG",
  "Top.GB.NZ",
  "Top.GB.AU",
  "Top.GB.BR",
]

const FUTURE_PARAM = [101, 901, 121, 107, 907, 171, 155]

const MAIN_INDEX_PARAM = "Top.Index.Main"
const JPN_INDEX_PARAM = "Jpn.Index.All"
const FX_PARAM = "Top.FX.JPY"
const COMMODITY_PARAM = "Top.Commodity"

const paramList = [
  { id: 2, param: MAIN_INDEX_PARAM },
  { id: 3, param: JPN_INDEX_PARAM },
  { id: 4, param: FOREIN_INDEX_PARAM },
  { id: 7, param: FX_PARAM },
  { id: 8, param: COMMODITY_PARAM },
  { id: 9, param: BOND_PARAM },
]

export const ChartSearchResultModal: React.FC<
  Props & { selectDomistic?: Function; selectForeign?: Function }
> = props => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [data, setData] = useState<Array<APIResponse.Res>>(null)
  //IEだと、先物選択後に他のタブを開くとスタイルがバグるので、先物のみ個別にステート管理
  const [futureData, setFutureData] = useState<Array<APIResponse.Res>>(null)
  const [selectItem, setSlectItem] = useState(0)
  const loadResistId = useMemo(() => uuid(), [])

  const resultList = get(props.result, "ResultList", [])
  const viewComponent = (id: number) => {
    const menuList =
      id === 4
        ? FOREIN_INDEX_MENU
        : id === 5
        ? FUTURE_MENU
        : id === 9
        ? BOND_MENU
        : null
    if (includes([0, 1], id))
      return (
        <Equity
          resultList={resultList}
          result={props.result}
          execute={props.execute}
          currentPage={props.currentPage}
          changePage={props.changePage}
          load={props.load}
          changeSearchValue={props.changeSearchValue}
          state={props.state}
        />
      )
    if (id === 6)
      return <Option execute={props.execute} loadResistId={loadResistId} />
    return (
      <Alternative
        data={data}
        futureData={futureData}
        execute={props.execute}
        selectedTab={selectedTab}
        selectItem={selectItem}
        setSlectItem={setSlectItem}
        menuList={menuList}
        title={HEADER_LABEL[selectedTab].name}
      />
    )
  }

  useEffect(() => {
    setSlectItem(0)
  }, [selectedTab])

  useEffect(() => {
    store.dispatch(pageLoadRegist(loadResistId, true))
    if (includes([0, 1], selectedTab)) {
      if (selectedTab === 0) props.selectDomistic()
      if (selectedTab === 1) props.selectForeign()
      store.dispatch(pageLoadRegist(loadResistId, false))
      return
    }
    if (includes([2, 3, 4, 7, 8, 9], selectedTab)) {
      const param = get(find(paramList, { id: selectedTab }), "param")
      if (!param) return
      ;(async () => {
        const res = await APIs.SymbolDetail.PriceList({
          Name: isArray(param) ? param[selectItem] : param,
        })
        selectedTab !== 9
          ? setData(chunk(get(res, "ResultList"), 12))
          : setData([get(res, "ResultList")])
        store.dispatch(pageLoadRegist(loadResistId, false))
      })()
    }
    if (selectedTab === 5) {
      if (!FUTURE_PARAM[selectItem]) {
        store.dispatch(pageLoadRegist(loadResistId, false))
        return
      }
      ;(async () => {
        const res = await APIs.SymbolDetail.SymbolList({
          Exchange: 2,
          TargetSecurityTypes: FUTURE_PARAM[selectItem],
          MajorMonthOnly: true,
        })
        setFutureData([get(res, "ResultList")])
        store.dispatch(pageLoadRegist(loadResistId, false))
      })()
    }
    if (selectedTab === 6) {
      store.dispatch(pageLoadRegist(loadResistId, false))
    }
  }, [selectedTab, selectItem])

  return (
    <Modal {...props} size="lg">
      <Modal.Header>
        <div className="modal-header chart-search-result-header">
          <div className="header-title">検索</div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-body-content chart-search-result-content">
          <div className="chart-search-result-label">
            {map(HEADER_LABEL, (items, index) => {
              return (
                <div
                  key={items.id}
                  className="btn-multi-control"
                  title={items.name}
                >
                  <label
                    className={ClassNames("btn btn-outline shaped", {
                      active: selectedTab === items.id,
                    })}
                  >
                    <input
                      type="checkbox"
                      className="input-multi-control"
                      onChange={() => {
                        setSelectedTab(items.id)
                        if (includes([0, 1], selectedTab))
                          setTimeout(() => {
                            props.load()
                          }, 100)
                      }}
                    />
                    <div>{items.name}</div>
                  </label>
                </div>
              )
            })}
          </div>
          {viewComponent(selectedTab)}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="modal-bottom-btn-row">
          <div
            className="btn btn-outline ripple"
            data-izimodal-close
            onClick={props.onHide}
          >
            閉じる
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

const Equity = (props: {
  resultList: Array<APIResponse.Res>
  execute: Function
  result: APIResponse.Res
  currentPage: number
  changePage: Function
  load: Function
  state: searchStateType
  changeSearchValue: Function
}) => {
  return (
    <>
      <div className="control-row filter-control-row">
        <SearchInput
          className="filter-control-item floating-input-field"
          value={props.state.searchValue}
          onChange={(str: string) => props.changeSearchValue(str)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.keyCode === KEY_CODE.ENTER) {
              props.changePage(0)
              props.load()
            }
          }}
        />
        <Btn
          className="btn-primary btn-shaped"
          name="検索"
          onClick={() => {
            props.changePage(0)
            props.load()
          }}
        />
      </div>
      <BaseTable
        className="table-striped table-search-result"
        list={tableColumns.utils.search}
        data={props.resultList}
        onClick={(data: APIResponse.Res) => props.execute(data)}
      />
      <Pagination
        list={props.resultList}
        total={get(props.result, "TotalCount", null)}
        current={props.currentPage}
        page={PAGE_NUM.PAGE_10}
        changePage={(page: number) => {
          props.changePage(page)
          props.load()
        }}
      />
    </>
  )
}

const Alternative = (props: {
  data: APIResponse.Res
  futureData: APIResponse.Res
  execute: Function
  selectedTab: number
  selectItem?: number
  setSlectItem?: Function
  menuList?: Array<object>
  title?: string
}) => {
  const {
    data,
    futureData,
    execute,
    selectItem,
    setSlectItem,
    menuList,
    selectedTab,
  } = props
  const menuFlag = !isEmpty(menuList)
  return (
    <div className="control-row filter-control-row">
      {menuFlag && (
        <div className={ClassNames("menu-list", { bond: selectedTab === 9 })}>
          {map(menuList, (items, index) => {
            return (
              <div
                className="menu-list-item"
                key={get(items, "id") || index}
                title={get(items, "name")}
                onClick={() => setSlectItem(get(items, "id"))}
              >
                <label className="radio-control">
                  <input
                    type="checkbox"
                    onChange={() => setSlectItem(get(items, "id"))}
                    checked={selectItem === get(items, "id")}
                  />
                  <span />
                </label>
                <div>{get(items, "name")}</div>
                <i className={ClassNames("icon-solid", get(items, "icon"))} />
              </div>
            )
          })}
        </div>
      )}
      <div
        className={ClassNames("modal-constent-list", {
          "menu-flag": menuFlag,
          future: selectedTab === 5,
        })}
      >
        <div className="modal-constent-table">
          <div className="modal-constent-list-title">
            <span>
              {menuFlag ? get(menuList, [selectItem, "name"]) : props.title}
            </span>
          </div>
          <div className="modal-constent-table-items">
            {map(
              selectedTab === 5 ? futureData : data,
              (_data, key: number) => {
                return (
                  <div
                    key={key}
                    className={ClassNames({
                      "table-item": includes([2, 3, 7, 8], selectedTab),
                      "main-index": selectedTab === 2,
                      exchange: includes([3, 7], selectedTab),
                    })}
                  >
                    <BaseTable
                      className="table-striped"
                      list={[
                        createColumns(
                          menuFlag
                            ? get(menuList, [selectItem, "name"])
                            : props.title,

                          "title-cell",
                          (row: object) => {
                            const icon = getCountryIcon(row)
                            const iconVisible = !includes(
                              [4, 5, 8, 9],
                              selectedTab
                            )
                            return (
                              <div
                                className="index-name-row"
                                onClick={() => {
                                  execute(row)
                                }}
                              >
                                {iconVisible && (
                                  <i
                                    className={ClassNames("icon-solid ", icon)}
                                  />
                                )}
                                <div className="index-name ellipsis-text">
                                  {get(row, "SymbolName")}
                                </div>
                              </div>
                            )
                          }
                        ),
                      ]}
                      data={_data as Array<object>}
                      noTitleFlag={true}
                    />
                  </div>
                )
              }
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const Option = (props: { execute: Function; loadResistId: string }) => {
  const [selectedYearMonth, setSelectedYearMonth] = useState(null)
  const [id, setId] = useState(0)
  const [resultList, setResultList] = useState([])
  const [putData, setPutData] = useState([])
  const [callData, setCallData] = useState([])
  const [stkList, setStkList] = useState([])
  const [yearMonthList, setYearMonthList] = useState([])
  const [filterList, setFilterList] = useState([])
  const [currentActivePosition, changeActivePosition] = useState<number>(-1)
  const [currentActivePutOrCall, changeActivePutOrCall] = useState<number>(-1)
  const changeFilter = (id: number) => {
    setId(id)
    setSelectedYearMonth(filterList[id])
  }

  useEffect(() => {
    ;(async () => {
      store.dispatch(pageLoadRegist(props.loadResistId, true))
      const data = await APIs.SymbolDetail.OptionSymbolList({
        TargetSecurityTypes: "103",
        Exchange: "2",
      })
      const yearMonth = get(data, "ResultList", [])
      setFilterList(yearMonth)
      setYearMonthList(
        map(yearMonth, (str: string, i: number) => {
          return { id: i, name: `${Formatter.date(str, "YYYY/MM")}限月` }
        })
      )
      setSelectedYearMonth(yearMonth[0])
      store.dispatch(pageLoadRegist(props.loadResistId, false))
    })()
  }, [])
  useEffect(() => {
    if (!selectedYearMonth) return
    ;(async () => {
      store.dispatch(pageLoadRegist(props.loadResistId, true))
      const data = await APIs.SymbolDetail.SymbolList({
        TargetSecurityTypes: "103",
        Exchange: "2",
        YearMonth: selectedYearMonth,
      })
      const resultList = get(data, "ResultList", [])
      setResultList(reverse(resultList))
      const putResultList = filter(resultList, {
        PutOrCall: PUT_OR_CALL.PUT.id,
      })
      const callResulList = filter(resultList, {
        PutOrCall: PUT_OR_CALL.CALL.id,
      })
      setPutData(putResultList)
      setCallData(callResulList)
      store.dispatch(pageLoadRegist(props.loadResistId, false))
    })()
  }, [selectedYearMonth])

  const callSymbolDetail = (symbol: any) => {
    if (symbol) {
      changeActivePosition(-1)
      changeActivePutOrCall(-1)
    }
  }

  useEffect(() => {
    setStkList(
      putData.map((data: APIResponse.Res) => get(data, "StrikePrice", null))
    )
  }, [putData])

  return (
    <div className="control-option-row filter-control-row">
      <div className="row-filter">
        <BaseSelect value={id} option={yearMonthList} onChange={changeFilter} />
      </div>
      <OptionTable
        callSymbolDetail={callSymbolDetail}
        call={callData}
        put={putData}
        stk={stkList}
        active={currentActivePosition}
        activePutOrCall={currentActivePutOrCall}
        onClick={async (symbol: any, index: number) => {
          props.execute(symbol)
        }}
        isModal={true}
      />
    </div>
  )
}

export const RelativeChartSearchResultModal: React.FC<
  Props & { selectDomistic?: Function; selectForeign?: Function }
> = props => {
  const searchCountryTypeContext = useContext(SearchCountryTypeContext)
  const selectedTab = searchCountryTypeContext.type
  const resultList = get(props.result, "ResultList", [])

  useEffect(() => {
    if (selectedTab === 0) props.selectDomistic()
    if (selectedTab === 1) props.selectForeign()
  }, [selectedTab])

  return (
    <Modal {...props} size="lg">
      <Modal.Header>
        <div className="modal-header chart-search-result-header">
          <div className="header-title">検索</div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-body-content chart-search-result-content">
          <Equity
            resultList={resultList}
            result={props.result}
            execute={props.execute}
            currentPage={props.currentPage}
            changePage={props.changePage}
            load={props.load}
            changeSearchValue={props.changeSearchValue}
            state={props.state}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="modal-bottom-btn-row">
          <div
            className="btn btn-outline ripple"
            data-izimodal-close
            onClick={props.onHide}
          >
            閉じる
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
